const HOUR = 60 * 60 * 1000;
const LIVE_TOLERANCE_MS = 30 * 60 * 1000;
const UPCOMING_WINDOW_MS = 24 * HOUR;

const SEVERITY_WEIGHT = Object.freeze({
    critical: 80,
    warning: 55,
    watch: 32,
    info: 10,
});

const KIND_WEIGHT = Object.freeze({
    convective_cell: 95,
    hail: 92,
    heavy_rain: 90,
    flash_flood: 90,
    ventilation_boundary: 86,
    strong_wind: 80,
    wildfire: 76,
    wildfire_risk: 72,
    saharan_dust: 64,
    surface_convergence: 58,
    marine_heatwave: 46,
    thermal_hotspot: 42,
    thermal_inversion: 24,
    land_breeze: 12,
    sea_breeze: 10,
});

const KIND_ICONS = Object.freeze({
    convective_cell: '⛈',
    hail: '◆',
    heavy_rain: '☔',
    flash_flood: '≋',
    ventilation_boundary: '⇓',
    strong_wind: '🌬',
    wildfire: '🔥',
    wildfire_risk: '🔥',
    saharan_dust: '🌫',
    surface_convergence: '↗',
    marine_heatwave: '≈',
    thermal_hotspot: '♨',
    thermal_inversion: '◒',
    land_breeze: '↘',
    sea_breeze: '↗',
});

const HEADLINE_KINDS = new Set([
    'convective_cell',
    'hail',
    'heavy_rain',
    'flash_flood',
    'ventilation_boundary',
    'strong_wind',
    'wildfire',
]);

const INHERENT_FORECAST_KINDS = new Set(['saharan_dust', 'wildfire_risk']);

function epochMs(value) {
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return Math.abs(numeric) < 1e11 ? numeric * 1000 : numeric;
    const parsed = Date.parse(String(value));
    return Number.isFinite(parsed) ? parsed : null;
}

function numberOrNull(value) {
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
}

function unique(values) {
    return [...new Set(values.filter(Boolean))];
}

function cleanText(value, limit = 190) {
    const text = String(value || '').replace(/\s+/g, ' ').replace(/…{2,}/g, '…').trim();
    if (text.length <= limit) return text;
    const sliced = text.slice(0, Math.max(0, limit - 1));
    const boundary = sliced.lastIndexOf(' ');
    return (boundary > limit * 0.65 ? sliced.slice(0, boundary) : sliced).replace(/[.…]+$/g, '') + '…';
}

function evidenceStrengthValue(raw) {
    const evidence = raw?.evidence_strength;
    if (!evidence || typeof evidence !== 'object') return null;
    return numberOrNull(evidence.overall ?? evidence.index);
}

export function eventTrustLabel(raw = {}) {
    const evidence = raw?.evidence_strength;
    if (evidence && typeof evidence === 'object') {
        const label = cleanText(evidence.label || 'multi-fonte', 45);
        return 'Evidenza ' + label;
    }
    const probability = numberOrNull(raw?.probability);
    if (probability !== null) {
        return 'Probabilità ' + Math.round(Math.max(0, Math.min(1, probability)) * 100) + '%';
    }
    const confidence = numberOrNull(raw?.confidence);
    if (confidence !== null && raw?.confidence_semantics === 'calibrated_probability') {
        return 'Probabilità ' + Math.round(Math.max(0, Math.min(1, confidence)) * 100) + '%';
    }
    return 'Confidenza non pubblicata';
}

function normalizePhase(raw, kind) {
    const phase = String(raw || '').toLowerCase();
    if (['observed', 'nowcast', 'forecast', 'potential'].includes(phase)) return phase;
    return INHERENT_FORECAST_KINDS.has(kind) ? 'forecast' : 'observed';
}

function sourceLabels(raw, origin) {
    const sources = Array.isArray(raw.sources) ? raw.sources : [];
    const labels = sources.map(source => {
        if (typeof source === 'string') return source;
        return source?.label || source?.name || source?.id || null;
    });
    if (typeof raw.source_labels === 'string') labels.push(...raw.source_labels.split(/\s*[+,]\s*/));
    if (!labels.length) labels.push(
        origin === 'relational' ? 'Intelligenza relazionale WS'
            : origin === 'territorial' ? 'Analisi territoriale WS'
            : origin === 'mesoscale' ? 'Diagnostica mesoscale WS'
                : 'Total Fusion WS',
    );
    return unique(labels.map(label => cleanText(label, 45)));
}

function narrativeText(value, limit = 180) {
    if (typeof value === 'string') return cleanText(value, limit);
    if (!value || typeof value !== 'object') return '';
    return cleanText(
        value.summary
        ?? value.text
        ?? value.statement_it
        ?? value.detail_it
        ?? value.description
        ?? value.title
        ?? value.label
        ?? '',
        limit,
    );
}

function narrativeTitle(value, limit = 80) {
    if (!value || typeof value !== 'object') return '';
    return cleanText(value.title ?? value.label ?? '', limit);
}

function relationalEventTimes(raw, referenceMs) {
    const current = raw.current_state && typeof raw.current_state === 'object' ? raw.current_state : {};
    const next = raw.next_change && typeof raw.next_change === 'object' ? raw.next_change : {};
    const detectedMs = epochMs(
        raw.detected_at_utc
        ?? current.detected_at_utc
        ?? current.valid_from_utc
        ?? raw.valid_from_utc
        ?? referenceMs,
    ) ?? referenceMs;
    const transitionStartMs = epochMs(
        raw.transition_from_utc
        ?? next.transition_from_utc
        ?? next.valid_from_utc,
    );
    const transitionEndMs = epochMs(
        raw.transition_to_utc
        ?? next.transition_to_utc
        ?? next.valid_to_utc
        ?? raw.valid_to_utc,
    );
    const currentEndMs = epochMs(
        raw.observation_valid_to_utc
        ?? current.observation_valid_to_utc
        ?? current.valid_to_utc,
    ) ?? transitionStartMs ?? epochMs(raw.valid_to_utc) ?? detectedMs + 2 * HOUR;
    const startCandidates = [detectedMs, transitionStartMs].filter(Number.isFinite);
    const endCandidates = [currentEndMs, transitionEndMs].filter(Number.isFinite);
    return {
        startMs: Math.min(...startCandidates),
        endMs: Math.max(...endCandidates),
        currentStartMs: detectedMs,
        currentEndMs: Math.max(detectedMs, currentEndMs),
        transitionStartMs,
        transitionEndMs,
    };
}

function eventTimes(raw, phase, referenceMs) {
    const startMs = epochMs(
        raw.valid_from_utc
        ?? raw.starts_utc
        ?? raw.start_utc
        ?? raw.observed_at
        ?? raw.valid_utc
        ?? referenceMs,
    ) ?? referenceMs;
    let endMs = epochMs(raw.valid_to_utc ?? raw.ends_utc ?? raw.end_utc);
    if (!Number.isFinite(endMs)) {
        endMs = startMs + (phase === 'observed' ? 2 * HOUR : phase === 'nowcast' ? 3 * HOUR : 6 * HOUR);
    }
    return {
        startMs: Math.min(startMs, endMs),
        endMs: Math.max(startMs, endMs),
    };
}

function normalizeEvent(raw, origin, referenceMs, index) {
    if (!raw || typeof raw !== 'object') return null;
    const kind = String(raw.kind || raw.type || raw.category || 'weather_signal').toLowerCase();
    const phase = normalizePhase(raw.phase, kind);
    const relational = origin === 'relational'
        || raw.current_state !== undefined
        || raw.next_change !== undefined;
    const times = relational
        ? relationalEventTimes(raw, referenceMs)
        : {
            ...eventTimes(raw, phase, referenceMs),
            currentStartMs: null,
            currentEndMs: null,
            transitionStartMs: null,
            transitionEndMs: null,
        };
    const lat = numberOrNull(raw.lat ?? raw.latitude);
    const lon = numberOrNull(raw.lon ?? raw.lng ?? raw.longitude);
    const rawEvidenceStrength = evidenceStrengthValue(raw);
    const probabilitySemantics = String(
        raw.probability_semantics
        || raw.confidence_semantics
        || 'unspecified',
    ).toLowerCase();
    const publishedProbability = probabilitySemantics === 'calibrated_probability'
        ? numberOrNull(raw.probability ?? raw.confidence)
        : null;
    const normalizedConfidence = publishedProbability === null
        ? null
        : Math.max(0, Math.min(1, publishedProbability));
    const stateDetail = raw.state_detail && typeof raw.state_detail === 'object'
        ? raw.state_detail.state ?? raw.state_detail.lifecycle ?? raw.state_detail.status
        : raw.state_detail;
    const currentStateText = narrativeText(raw.current_state, 180)
        || (relational ? cleanText(raw.summary || '', 180) : '');
    const nextChangeText = narrativeText(raw.next_change, 170);
    return {
        id: String(raw.id || origin + '-' + kind + '-' + index),
        diagnosisKey: raw.diagnosis_key ? String(raw.diagnosis_key) : null,
        episodeId: raw.episode_id ? String(raw.episode_id) : null,
        revision: numberOrNull(raw.revision),
        updatedAtMs: epochMs(raw.updated_at_utc ?? raw.generated_at_utc ?? raw.detected_at_utc),
        origin,
        relational,
        kind,
        subtype: String(raw.subtype || '').toLowerCase(),
        phase,
        status: String(raw.lifecycle ?? stateDetail ?? raw.status ?? '').toLowerCase(),
        severity: String(raw.severity || 'info').toLowerCase(),
        confidence: normalizedConfidence,
        evidenceStrength: rawEvidenceStrength,
        communicationEligible: raw.communication_eligible !== false,
        headlineEligible: raw.headline_eligible === true
            || (!relational && HEADLINE_KINDS.has(kind)),
        communicationPriority: numberOrNull(raw.communication_priority),
        title: cleanText(raw.title || raw.label || kind.replaceAll('_', ' '), 80),
        summary: cleanText(raw.summary || raw.explanation || '', 260),
        currentStateText,
        nextChangeText,
        currentStateTitle: narrativeTitle(raw.current_state),
        nextChangeTitle: narrativeTitle(raw.next_change),
        evidence: (Array.isArray(raw.evidence_summary) && raw.evidence_summary.length
            ? raw.evidence_summary
            : Array.isArray(raw.evidence) ? raw.evidence : []).map(value =>
            narrativeText(value, 150),
        ).filter(Boolean),
        limitations: unique([
            ...(Array.isArray(raw.contradictions) ? raw.contradictions : []),
            ...(Array.isArray(raw.limitations) ? raw.limitations : []),
        ].map(value => narrativeText(value, 170)).filter(Boolean)),
        sources: sourceLabels(raw, origin),
        areas: Array.isArray(raw.areas) ? raw.areas.map(area => cleanText(area, 50)) : [],
        lat,
        lon,
        radiusKm: numberOrNull(raw.radius_km),
        corridor: raw.corridor?.type === 'LineString' ? raw.corridor : null,
        trustLabel: eventTrustLabel(raw),
        ...times,
    };
}

export function newerDiagnosisSnapshot(
    candidate,
    current,
    candidateOrigin = candidate?.origin,
    currentOrigin = current?.origin,
) {
    const candidateEpisode = String(
        candidate?.episodeId ?? candidate?.episode_id ?? '',
    );
    const currentEpisode = String(
        current?.episodeId ?? current?.episode_id ?? '',
    );
    const sameOrUnknownEpisode = (
        !candidateEpisode
        || !currentEpisode
        || candidateEpisode === currentEpisode
    );
    const candidateRevision = candidate.revision ?? -Infinity;
    const currentRevision = current.revision ?? -Infinity;
    if (sameOrUnknownEpisode && candidateRevision !== currentRevision) {
        return candidateRevision > currentRevision;
    }
    const candidateUpdated = epochMs(
        candidate.updatedAtMs
        ?? candidate.updated_at_utc
        ?? candidate.generated_at_utc
        ?? candidate.detected_at_utc,
    ) ?? -Infinity;
    const currentUpdated = epochMs(
        current.updatedAtMs
        ?? current.updated_at_utc
        ?? current.generated_at_utc
        ?? current.detected_at_utc,
    ) ?? -Infinity;
    if (candidateUpdated !== currentUpdated) return candidateUpdated > currentUpdated;
    const originRank = {relational: 3, mesoscale: 2, territorial: 1, fusion: 0};
    if ((originRank[candidateOrigin] ?? 0) !== (originRank[currentOrigin] ?? 0)) {
        return (originRank[candidateOrigin] ?? 0) > (originRank[currentOrigin] ?? 0);
    }
    return String(candidate.id || '').localeCompare(String(current.id || '')) < 0;
}

function canonicalRelationalEvents(events) {
    const selected = new Map();
    for (const event of events) {
        const key = event.diagnosisKey || event.id;
        const current = selected.get(key);
        if (!current || newerDiagnosisSnapshot(event, current)) selected.set(key, event);
    }
    return [...selected.values()].sort((a, b) =>
        (a.diagnosisKey || a.id).localeCompare(b.diagnosisKey || b.id),
    );
}

export function collectBriefingEvents({state = {}, territorialDocument = null} = {}) {
    const referenceMs = epochMs(state.generated_at_utc) ?? Date.now();
    const relationalRaw = Array.isArray(state?.relational_intelligence?.top_diagnoses)
        ? state.relational_intelligence.top_diagnoses
        : [];
    const relational = relationalRaw
        .map((event, index) => normalizeEvent(event, 'relational', referenceMs, index))
        .filter(Boolean);
    const mesoscaleRaw = Array.isArray(state?.mesoscale_intelligence?.top_events)
        ? state.mesoscale_intelligence.top_events
        : [];
    const mesoscale = mesoscaleRaw
        .map((event, index) => normalizeEvent(event, 'mesoscale', referenceMs, index))
        .filter(Boolean);
    const territorialRaw = Array.isArray(territorialDocument?.events)
        ? territorialDocument.events
        : [];
    const territorial = territorialRaw
        .map((event, index) => normalizeEvent(event, 'territorial', referenceMs, index))
        .filter(Boolean);
    const diagnoses = canonicalRelationalEvents(
        [...relational, ...mesoscale, ...territorial].filter(event => event.diagnosisKey),
    );
    const unkeyedRelational = canonicalRelationalEvents(
        relational.filter(event => !event.diagnosisKey),
    );
    const canonicalIds = new Set([...diagnoses, ...unkeyedRelational].map(event => event.id));
    const unkeyedMesoscale = mesoscale.filter(event =>
        !event.diagnosisKey && !canonicalIds.has(event.id),
    );
    const sourceIds = new Set([...canonicalIds, ...unkeyedMesoscale.map(event => event.id)]);
    const unkeyedTerritorial = territorial.filter(event =>
        !event.diagnosisKey && !sourceIds.has(event.id),
    );
    const canonical = [...diagnoses, ...unkeyedRelational, ...unkeyedMesoscale, ...unkeyedTerritorial];
    const canonicalKeys = new Set(
        canonical.map(event => event.kind + '|' + event.phase),
    );
    const fusionRaw = Array.isArray(state?.live?.total_fusion?.insights)
        ? state.live.total_fusion.insights
        : [];
    const fusion = fusionRaw
        .map((event, index) => normalizeEvent(event, 'fusion', referenceMs, index))
        .filter(event => event && !canonicalKeys.has(event.kind + '|' + event.phase));
    const seen = new Set();
    return [...canonical, ...fusion].filter(event => {
        const key = event.diagnosisKey ? 'diagnosis:' + event.diagnosisKey : 'id:' + event.id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

function withinSicilyContext(event) {
    if (!Number.isFinite(event.lat) || !Number.isFinite(event.lon)) return true;
    return event.lat >= 35.1 && event.lat <= 39.8 && event.lon >= 11.2 && event.lon <= 17.7;
}

function eventScore(event, targetMs) {
    const kind = KIND_WEIGHT[event.kind] ?? 30;
    const severity = SEVERITY_WEIGHT[event.severity] ?? 10;
    const derivedPriority = kind * 0.65 + severity * 0.35;
    const communicationPriority = event.communicationPriority === null
        ? derivedPriority
        : Math.max(0, Math.min(100, event.communicationPriority));
    // A pressing boundary is an observed spatial transition, not generic wind severity.
    // Rank it above generic forecast warnings without calling the boundary a warning.
    const lifecycle = event.status === 'pressing_south' ? 25 : 0;
    // Uncalibrated evidence strength may rank a published fact, but must never
    // be exposed to the UI as a probability or meteorological confidence.
    const confidence = (event.confidence ?? event.evidenceStrength ?? 0) * 12;
    const local = withinSicilyContext(event) ? 15 : -45;
    const waitHours = Math.max(0, (event.startMs - targetMs) / HOUR);
    const imminence = Math.max(0, 18 - waitHours);
    return communicationPriority * 3 + severity * 0.35
        + lifecycle + confidence + local + imminence;
}

function isActive(event, targetMs, mode) {
    const tolerance = mode === 'live' && event.phase === 'observed' ? LIVE_TOLERANCE_MS : 0;
    return event.startMs <= targetMs + tolerance && event.endMs >= targetMs - tolerance;
}

function isRelationalCurrent(event, targetMs) {
    return event.relational === true
        && Number.isFinite(event.currentStartMs)
        && Number.isFinite(event.currentEndMs)
        && event.currentStartMs <= targetMs
        && event.currentEndMs >= targetMs;
}

function isRelationalTransition(event, targetMs) {
    return event.relational === true
        && Number.isFinite(event.transitionStartMs)
        && Number.isFinite(event.transitionEndMs)
        && event.transitionStartMs <= targetMs
        && event.transitionEndMs >= targetMs;
}

function groupEvents(events, targetMs) {
    const mergeGapMs = 30 * 60 * 1000;
    const groups = [];
    const ordered = events.slice().sort((a, b) =>
        a.kind.localeCompare(b.kind)
        || a.startMs - b.startMs
        || a.endMs - b.endMs
        || a.id.localeCompare(b.id),
    );
    for (const event of ordered) {
        let current = groups.find(group =>
            group.kind === event.kind
            && (!event.diagnosisKey && !group.diagnosisKey
                || event.diagnosisKey === group.diagnosisKey)
            && event.startMs <= group.endMs + mergeGapMs
            && event.endMs >= group.startMs - mergeGapMs,
        );
        if (!current) {
            current = {
                kind: event.kind,
                diagnosisKey: event.diagnosisKey,
                events: [],
                representative: event,
                startMs: event.startMs,
                endMs: event.endMs,
            };
            groups.push(current);
        }
        current.events.push(event);
        current.startMs = Math.min(current.startMs, event.startMs);
        current.endMs = Math.max(current.endMs, event.endMs);
        const eventRank = eventScore(event, targetMs);
        const currentRank = eventScore(current.representative, targetMs);
        if (eventRank > currentRank
            || (eventRank === currentRank && event.id.localeCompare(current.representative.id) < 0)) {
            current.representative = event;
        }
    }
    return groups.sort((a, b) => {
        const rank = eventScore(b.representative, targetMs) - eventScore(a.representative, targetMs);
        return rank || a.startMs - b.startMs || a.representative.id.localeCompare(b.representative.id);
    });
}

function localTime(ms, options = {}) {
    return new Intl.DateTimeFormat('it-IT', {
        timeZone: 'Europe/Rome',
        ...options,
    }).format(new Date(ms));
}

function eventTimeLabel(group, targetMs) {
    if (group.events.some(event => isRelationalCurrent(event, targetMs))) return 'Adesso';
    let startMs = Math.min(...group.events.map(event => event.startMs));
    let endMs = Math.max(...group.events.map(event => event.endMs));
    if (group.representative.relational) {
        if (Number.isFinite(group.representative.transitionStartMs)) {
            startMs = group.representative.transitionStartMs;
        }
        if (Number.isFinite(group.representative.transitionEndMs)) {
            endMs = group.representative.transitionEndMs;
        }
    }
    if (startMs <= targetMs && endMs >= targetMs) {
        if (group.representative.phase === 'observed') return 'Adesso';
        const targetDay = localTime(targetMs, {year: 'numeric', month: '2-digit', day: '2-digit'});
        const endDay = localTime(endMs, {year: 'numeric', month: '2-digit', day: '2-digit'});
        const tomorrowDay = localTime(targetMs + 24 * HOUR, {year: 'numeric', month: '2-digit', day: '2-digit'});
        const clock = localTime(endMs, {hour: '2-digit', minute: '2-digit'});
        if (endDay === targetDay) return 'Fino alle ' + clock;
        if (endDay === tomorrowDay) return 'Fino a domani, ' + clock;
        return 'Fino a ' + localTime(endMs, {weekday: 'short', day: '2-digit', month: '2-digit'})
            + ', ' + clock;
    }
    const delta = startMs - targetMs;
    if (delta <= HOUR) return 'Entro un’ora';
    const sameDay = localTime(startMs, {year: 'numeric', month: '2-digit', day: '2-digit'})
        === localTime(targetMs, {year: 'numeric', month: '2-digit', day: '2-digit'});
    if (sameDay) return 'Dalle ' + localTime(startMs, {hour: '2-digit', minute: '2-digit'});
    const tomorrow = localTime(targetMs + 24 * HOUR, {year: 'numeric', month: '2-digit', day: '2-digit'})
        === localTime(startMs, {year: 'numeric', month: '2-digit', day: '2-digit'});
    return (tomorrow ? 'Domani ' : localTime(startMs, {weekday: 'short', day: '2-digit', month: '2-digit'}) + ' · ')
        + localTime(startMs, {hour: '2-digit', minute: '2-digit'});
}

function transitionWindowLabel(event) {
    if (!Number.isFinite(event.transitionStartMs)) return '';
    const from = localTime(event.transitionStartMs, {hour: '2-digit', minute: '2-digit'});
    if (!Number.isFinite(event.transitionEndMs)) return 'dalle ' + from;
    const to = localTime(event.transitionEndMs, {hour: '2-digit', minute: '2-digit'});
    const sameDay = localTime(event.transitionStartMs, {year: 'numeric', month: '2-digit', day: '2-digit'})
        === localTime(event.transitionEndMs, {year: 'numeric', month: '2-digit', day: '2-digit'});
    if (sameDay) return 'tra le ' + from + ' e le ' + to;
    return 'da ' + localTime(event.transitionStartMs, {weekday: 'short', hour: '2-digit', minute: '2-digit'})
        + ' a ' + localTime(event.transitionEndMs, {weekday: 'short', hour: '2-digit', minute: '2-digit'});
}

function relationalDescription(event, targetMs) {
    const current = event.currentStateText || event.summary;
    const next = event.nextChangeText;
    if (isRelationalTransition(event, targetMs)) return cleanText(next || event.summary, 320);
    if (!next) return cleanText(current || event.summary, 320);
    const window = transitionWindowLabel(event);
    const prefix = 'Prossimo cambiamento' + (window ? ' ' + window : '') + ': ';
    const currentPrefix = isRelationalCurrent(event, targetMs) && current ? current + ' ' : '';
    return cleanText(currentPrefix + prefix + next, 320);
}

function deriveWhere(event) {
    if (event.areas.length) return event.areas.slice(0, 3).join(', ');
    const locality = event.summary.match(/localit(?:à|a):\s*([^.]+)/i)?.[1];
    if (locality) return cleanText(locality.split(',').slice(0, 3).join(', '), 70);
    const evidencePlace = event.evidence[0]?.match(/^([^:]{2,55}):/)?.[1];
    if (evidencePlace) return evidencePlace.replaceAll('_', ' ');
    if (!Number.isFinite(event.lat) || !Number.isFinite(event.lon)) return '';
    if (withinSicilyContext(event)) return '';
    if (event.lon < 11.2) return 'a sud-ovest della Sicilia';
    if (event.lon > 17.7) return 'a est della Sicilia';
    if (event.lat < 35.1) return 'a sud della Sicilia';
    return 'a nord della Sicilia';
}

function maxGust(events) {
    const values = events.flatMap(event => event.evidence.flatMap(text =>
        [...text.matchAll(/raffica\s+(\d+(?:[.,]\d+)?)/gi)].map(match => Number(match[1].replace(',', '.'))),
    )).filter(Number.isFinite);
    return values.length ? Math.round(Math.max(...values)) : null;
}

function groupDescription(group, targetMs) {
    const event = group.representative;
    const potentialPrefix = event.phase === 'potential' ? 'Possibile evoluzione: ' : '';
    if (event.relational) return relationalDescription(event, targetMs);
    if (event.kind === 'ventilation_boundary' && event.summary) {
        return cleanText(event.summary, 230);
    }
    if (event.kind === 'strong_wind') {
        const gust = maxGust(group.events);
        const prefix = gust ? 'Raffiche fino a ' + gust + ' km/h. ' : '';
        return cleanText(potentialPrefix + prefix + event.summary, 185);
    }
    if (event.kind === 'convective_cell' && event.evidence.length) {
        return cleanText(potentialPrefix + event.summary + ' ' + event.evidence.slice(0, 2).join(' · '), 185);
    }
    if (event.kind === 'thermal_hotspot') {
        return group.events.length + ' segnali termici satellitari da verificare sul territorio.';
    }
    if (event.summary) return cleanText(potentialPrefix + event.summary, 185);
    if (event.evidence.length) return cleanText(event.evidence.slice(0, 2).join(' · '), 185);
    return 'Segnale meteorologico rilevato dai sistemi Weather Sicily.';
}

function confidenceLabel(value) {
    if (!Number.isFinite(value)) return 'non disponibile';
    if (value >= 0.8) return 'alta';
    if (value >= 0.6) return 'media';
    return 'limitata';
}

function groupToItem(group, targetMs) {
    const event = group.representative;
    const where = deriveWhere(event);
    const noisyCountKinds = new Set(['thermal_hotspot', 'thermal_inversion', 'sea_breeze', 'land_breeze']);
    const count = group.events.length;
    const temporalTitle = isRelationalTransition(event, targetMs) && event.nextChangeTitle
        ? event.nextChangeTitle
        : isRelationalCurrent(event, targetMs) && event.currentStateTitle
            ? event.currentStateTitle
            : event.title;
    const statedTitle = event.phase === 'potential'
        ? 'Possibile ' + temporalTitle.charAt(0).toLowerCase() + temporalTitle.slice(1)
        : temporalTitle;
    const title = event.kind === 'ventilation_boundary'
        ? statedTitle
        : noisyCountKinds.has(event.kind) && count > 1
            ? statedTitle + ' · ' + count + ' aree'
            : statedTitle + (where ? ' · ' + where : '');
    const evidenceStrengths = group.events.map(item => item.evidenceStrength).filter(Number.isFinite);
    const hasUncalibratedEvidence = evidenceStrengths.length > 0;
    const publishedConfidences = hasUncalibratedEvidence
        ? []
        : group.events.map(item => item.confidence).filter(Number.isFinite);
    const confidence = publishedConfidences.length ? Math.max(...publishedConfidences) : null;
    return Object.freeze({
        id: event.id,
        diagnosisKey: event.diagnosisKey,
        origin: event.origin,
        kind: event.kind,
        phase: event.phase,
        severity: event.severity,
        subtype: event.subtype,
        status: event.status,
        headlineEligible: event.headlineEligible,
        communicationPriority: event.communicationPriority,
        icon: KIND_ICONS[event.kind] || '◆',
        title: cleanText(title, 100),
        shortTitle: temporalTitle,
        description: groupDescription(group, targetMs),
        timeLabel: eventTimeLabel(group, targetMs),
        confidence,
        evidenceStrength: evidenceStrengths.length ? Math.max(...evidenceStrengths) : null,
        trustLabel: event.trustLabel,
        confidenceLabel: confidenceLabel(confidence),
        sources: unique(group.events.flatMap(item => item.sources)),
        limitations: unique(group.events.flatMap(item => item.limitations)).slice(0, 2),
        evidence: unique(group.events.flatMap(item => item.evidence)).slice(0, 3),
        currentState: event.currentStateText,
        nextChange: event.nextChangeText,
        lat: event.lat,
        lon: event.lon,
        radiusKm: event.radiusKm,
        corridor: event.corridor,
        local: withinSicilyContext(event),
        score: eventScore(event, targetMs),
    });
}

function scenarioCoverage(state) {
    const timestamps = (state?.synoptic_scenario?.timesteps || [])
        .map(step => epochMs(step?.valid_utc))
        .filter(Number.isFinite)
        .sort((a, b) => a - b);
    if (!timestamps.length) return null;
    return {startMs: timestamps[0], endMs: timestamps[timestamps.length - 1]};
}

function nowcastDecisionEligible(state) {
    const nowcast = state?.absolute_engine?.nowcast;
    return nowcast?.cycle_ok === true && nowcast?.authoritative === true;
}

function eventAuthorityEligible(event, state) {
    // Il broker nowcast governa celle ed estrapolazioni WS Now, non i tracker
    // relazionali che pubblicano un proprio gate di comunicazione.
    if (event.relational || event.kind === 'ventilation_boundary') {
        return event.communicationEligible;
    }
    if (event.phase === 'nowcast') return nowcastDecisionEligible(state);
    return true;
}

function freshnessLabel(state, nowMs) {
    const generatedMs = epochMs(state.generated_at_utc ?? state.live_generated_at_utc);
    if (!Number.isFinite(generatedMs)) return 'aggiornamento non disponibile';
    const ageMinutes = Math.max(0, Math.round((nowMs - generatedMs) / 60000));
    if (ageMinutes < 1) return 'aggiornato ora';
    if (ageMinutes === 1) return 'aggiornato 1 min fa';
    if (ageMinutes < 60) return 'aggiornato ' + ageMinutes + ' min fa';
    return 'aggiornato alle ' + localTime(generatedMs, {hour: '2-digit', minute: '2-digit'});
}

function stableObservation(state) {
    const stations = state?.live?.stations || {};
    const raining = Array.isArray(stations.raining) ? stations.raining.length : 0;
    const gusts = Array.isArray(stations.strong_gusts) ? stations.strong_gusts.length : 0;
    if (raining || gusts) {
        const parts = [];
        if (raining) parts.push(raining + (raining === 1 ? ' stazione segnala pioggia' : ' stazioni segnalano pioggia'));
        if (gusts) parts.push(gusts + (gusts === 1 ? ' raffica forte' : ' raffiche forti'));
        return parts.join(' · ') + '.';
    }
    return 'La rete live non segnala piogge diffuse né raffiche forti sulla Sicilia.';
}

function contextLabel(mode, targetMs) {
    if (mode === 'live') return 'Sicilia · ORA';
    const prefix = mode === 'history' || mode === 'all' ? 'RICOSTRUZIONE' : 'SCENARIO';
    return 'Sicilia · ' + prefix + ' '
        + localTime(targetMs, {weekday: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'}).toUpperCase();
}

function unavailableBriefing(state, mode, targetMs, nowMs) {
    return Object.freeze({
        available: false,
        mode,
        targetMs,
        contextLabel: contextLabel(mode, targetMs),
        updatedLabel: freshnessLabel(state, nowMs),
        tone: 'unavailable',
        headline: 'Scenario non disponibile per questa scadenza.',
        summary: 'I dati terminano prima dell’istante selezionato: la mappa non mantiene fermo l’ultimo quadro.',
        now: [],
        next: [],
        confidence: null,
        confidenceLabel: 'non disponibile',
        evidenceStrength: null,
        trustLabel: 'Confidenza non pubblicata',
        sourceSummary: 'Copertura temporale terminata',
        evidence: [],
        proof: [],
        uncertainty: 'Nessuna estrapolazione automatica oltre i dati pubblicati.',
    });
}

export function composeWeatherBriefing({
    state = {},
    territorialDocument = null,
    targetMs = null,
    mode = 'live',
    nowMs = Date.now(),
} = {}) {
    const normalizedMode = ['history', 'all'].includes(String(mode)) ? 'history'
        : String(mode) === 'nowcast' ? 'nowcast'
            : String(mode) === 'forecast' ? 'forecast' : 'live';
    const referenceMs = epochMs(targetMs)
        ?? epochMs(state?.synoptic_scenario?.timesteps?.[state?.synoptic_scenario?.now_index]?.valid_utc)
        ?? epochMs(state.generated_at_utc)
        ?? nowMs;
    const coverage = scenarioCoverage(state);
    if (normalizedMode !== 'live' && coverage
        && (referenceMs < coverage.startMs || referenceMs > coverage.endMs)) {
        return unavailableBriefing(state, normalizedMode, referenceMs, nowMs);
    }

    const authoritativeEvents = collectBriefingEvents({state, territorialDocument})
        .filter(event => eventAuthorityEligible(event, state));
    const events = coverage
        ? authoritativeEvents
            .filter(event => event.endMs >= coverage.startMs && event.startMs <= coverage.endMs)
            .map(event => ({
                ...event,
                startMs: Math.max(event.startMs, coverage.startMs),
                endMs: Math.min(event.endMs, coverage.endMs),
            }))
        : authoritativeEvents;
    const currentEvents = events.filter(event => {
        if (!isActive(event, referenceMs, normalizedMode)) return false;
        if (event.relational) {
            if (normalizedMode === 'live') return isRelationalCurrent(event, referenceMs);
            if (normalizedMode === 'history') return false;
            return isRelationalCurrent(event, referenceMs)
                || isRelationalTransition(event, referenceMs);
        }
        if (normalizedMode === 'live') {
            return event.phase === 'observed';
        }
        if (normalizedMode === 'history') return event.phase === 'observed';
        return event.phase !== 'observed';
    });
    const nextEvents = events.filter(event => {
        if (event.phase === 'observed') return false;
        if (event.endMs < referenceMs) return false;
        // A hybrid relational diagnosis already explains its next change in
        // the current card. Repeating it in both lanes makes the answer look
        // like two unrelated events.
        if (isRelationalCurrent(event, referenceMs)) return false;
        // In a forecast frame an event already active belongs to "Adesso".
        // Repeating that exact episode in "Prossime ore" obscures the answer.
        if (normalizedMode !== 'live') {
            const activeInSelectedFrame = event.relational
                ? isRelationalCurrent(event, referenceMs)
                    || isRelationalTransition(event, referenceMs)
                : isActive(event, referenceMs, normalizedMode);
            if (activeInSelectedFrame) return false;
        }
        const upcomingStartMs = event.relational && Number.isFinite(event.transitionStartMs)
            ? event.transitionStartMs
            : event.startMs;
        return upcomingStartMs <= referenceMs + UPCOMING_WINDOW_MS;
    });

    const current = groupEvents(currentEvents, referenceMs).map(group => groupToItem(group, referenceMs));
    const next = groupEvents(nextEvents, referenceMs).map(group => groupToItem(group, referenceMs));
    const leadNow = current.find(item => item.local && item.headlineEligible);
    const leadNext = next.find(item => item.local && item.headlineEligible)
        || next.find(item => item.local)
        || next[0]
        || null;
    const fusion = state?.live?.total_fusion || {};
    const fresh = numberOrNull(fusion.fresh_source_count) ?? 0;
    const total = numberOrNull(fusion.source_count) ?? 0;

    let headline;
    let summary;
    let tone = 'calm';
    let lead = leadNow;
    if (normalizedMode === 'live') {
        if (leadNow) {
            headline = leadNow.title;
            summary = leadNow.description;
            tone = leadNow.severity;
        } else if (leadNext?.kind === 'ventilation_boundary'
            || leadNext?.diagnosisKey && leadNext.headlineEligible) {
            headline = leadNext.title;
            summary = leadNext.description;
            tone = leadNext.severity;
            lead = leadNext;
        } else if (leadNext) {
            headline = 'Nessun fenomeno severo confermato. ' + leadNext.shortTitle + ' da seguire.';
            summary = leadNext.description + ' ' + leadNext.timeLabel + '.';
            tone = leadNext.severity === 'critical' ? 'warning' : leadNext.severity;
            lead = leadNext;
        } else {
            headline = 'Nessun fenomeno severo confermato sulla Sicilia.';
            summary = stableObservation(state);
        }
    } else if (current.length) {
        lead = current[0];
        headline = lead.title;
        summary = lead.description;
        tone = lead.severity;
    } else if (leadNext) {
        lead = leadNext;
        headline = 'Nessun segnale dominante a questa scadenza.';
        summary = 'Il primo elemento successivo è ' + leadNext.shortTitle.toLowerCase() + ': ' + leadNext.description;
        tone = 'calm';
    } else {
        headline = 'Nessun fenomeno significativo previsto per questa scadenza.';
        summary = 'I sistemi non evidenziano un segnale territoriale dominante nei dati disponibili.';
    }

    const visibleNow = current
        .filter(item => !['sea_breeze', 'land_breeze'].includes(item.kind))
        .slice(0, 1);
    const visibleNowKinds = new Set(visibleNow.map(item => item.kind));
    const visibleNext = next.filter((item, index, items) =>
        items.findIndex(candidate =>
            (item.diagnosisKey && candidate.diagnosisKey === item.diagnosisKey)
            || (!item.diagnosisKey && candidate.kind === item.kind),
        ) === index,
    ).slice(0, 2).map(item => visibleNowKinds.has(item.kind)
        ? Object.freeze({...item, title: 'Nuovo episodio · ' + item.title})
        : item);
    // Source freshness is system health, not meteorological confidence.
    // Only a confidence explicitly published by the selected fact is shown.
    const confidence = lead?.confidence ?? null;
    const selected = unique(lead?.sources || []).slice(0, 5);
    const uncertainty = lead?.limitations?.[0]
        || (lead && lead.phase !== 'observed'
            ? 'Indicazione previsionale: non è ancora una conferma osservativa.'
            : total > 0 && fresh < total
                ? (total - fresh) + ' fonti non risultano fresche.'
                : 'Nessuna contraddizione rilevante pubblicata per il segnale principale.');

    return Object.freeze({
        available: true,
        mode: normalizedMode,
        targetMs: referenceMs,
        contextLabel: contextLabel(normalizedMode, referenceMs),
        updatedLabel: freshnessLabel(state, nowMs),
        tone,
        headline: cleanText(headline, 130),
        summary: cleanText(summary, 320),
        now: visibleNow,
        next: visibleNext,
        confidence,
        confidenceLabel: confidenceLabel(confidence),
        evidenceStrength: lead?.evidenceStrength ?? null,
        trustLabel: lead?.trustLabel || 'Confidenza non pubblicata',
        sourceSummary: total > 0
            ? fresh + '/' + total + ' fonti live' + (selected.length ? ' · ' + selected.join(' + ') : '')
            : selected.join(' + ') || 'Fonti in verifica',
        evidence: selected,
        proof: lead?.evidence || [],
        uncertainty,
    });
}
